/**
 * Diverging stacked-bar geometry shared by RACE SUMMARY's finish gap breakdown and the maneuver SUMMARY
 * view: signed parts per row, positives stacked right of zero (largest first), negatives left, all rows
 * on one scale so a single zero line runs through every bar.
 */

export interface DivergingBucket {
  key: string;
  label: string;
  /** CSS custom property holding the bucket colour, e.g. `--color-race-driver-start`. */
  token: string;
}

export interface DivergingScale {
  /** Value → horizontal position in % of the track. */
  x: (v: number) => number;
  ticks: number[];
}

export interface DivergingSegment {
  key: string;
  label: string;
  token: string;
  v: number;
  left: number;
  width: number;
}

/** Shared scale from the largest summed negative to the largest summed positive, rounded to `step(span)`. */
export function divergingScale(
  partsList: ReadonlyArray<Record<string, number>>,
  buckets: readonly DivergingBucket[],
  step: (span: number) => number
): DivergingScale {
  let lo = 0;
  let hi = 0;
  for (const parts of partsList) {
    let pos = 0;
    let neg = 0;
    for (const { key } of buckets) {
      const v = parts[key];
      if (v > 0) {pos += v;} else if (v < 0) {neg += v;}
    }
    hi = Math.max(hi, pos);
    lo = Math.min(lo, neg);
  }
  const s = step(hi - lo);
  hi = Math.max(s, Math.ceil(hi / s) * s);
  lo = Math.floor(lo / s) * s;
  const ticks: number[] = [];
  // Index-based so a fractional step cannot drift past `hi` through float accumulation.
  for (let i = 0; lo + i * s <= hi + s * 1e-9; i++) {ticks.push(lo + i * s);}
  return { x: (v: number) => ((v - lo) / (hi - lo)) * 100, ticks };
}

/** Tick step from 1/2/5 × 10^k giving roughly `targetTicks` intervals over `span`. */
export function niceStep(span: number, targetTicks = 5): number {
  if (!(span > 0)) {return 1;}
  const raw = span / targetTicks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const f = raw / mag;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * mag;
}

export function divergingSegments(
  parts: Record<string, number>,
  buckets: readonly DivergingBucket[],
  scale: DivergingScale
): DivergingSegment[] {
  const x = scale.x;
  const vals = buckets.map((b) => ({ ...b, v: parts[b.key] }));
  const out: DivergingSegment[] = [];
  let right = 0;
  for (const s of vals.filter((s) => s.v > 0).sort((a, b) => b.v - a.v)) {
    out.push({ key: s.key, label: s.label, token: s.token, v: s.v, left: x(right), width: x(right + s.v) - x(right) });
    right += s.v;
  }
  let left = 0;
  for (const s of vals.filter((s) => s.v < 0).sort((a, b) => a.v - b.v)) {
    out.push({ key: s.key, label: s.label, token: s.token, v: s.v, left: x(left + s.v), width: x(left) - x(left + s.v) });
    left += s.v;
  }
  return out;
}
